import { useCallback, useEffect, useRef, useState } from 'react'

// Native TTS: @capacitor-community/text-to-speech@6.1.0 (Capacitor 7 wali line)
// Browser/TV: Web Speech API

// Indian accent ke liye preference order (default Hindi)
const PREFERRED_LANGS = ['hi-IN', 'en-IN']

// plugin ki QueueStrategy: 0 = Flush (chalti awaaz kaat do), 1 = Add (queue me lagao)
const QUEUE_FLUSH = 0
const QUEUE_ADD = 1

/* ------------------------------------------------------------------ *
 *  TEXT NORMALIZE  --  "ek ek shabd/akshar bolne" ka asli fix yahin hai
 * ------------------------------------------------------------------ */

// TTS engine ALL-CAPS word ko acronym maan leta hai:
// "RAHUL" -> "R A H U L". Title case karte hi pura naam bolta hai.
const toTitleCase = (s = '') =>
  s.replace(/[A-Za-z][A-Za-z'’-]*/g, (w) =>
    w.length > 1 && w === w.toUpperCase()
      ? w.charAt(0) + w.slice(1).toLowerCase()
      : w
  )

// naam ko bolne layak banao (DB se aksar CAPS / dots / extra space aata hai)
export const cleanName = (raw = '') => {
  let s = String(raw)
    .replace(/[_/\\|*#~^<>[\]{}()"]+/g, ' ')   // symbols jinhe engine akshar-akshar padhta hai
    .replace(/\s*\.\s*/g, ' ')                 // "K.G." -> "K G"  (full stop = lamba pause)
    .replace(/[,;:]+/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim()

  s = toTitleCase(s)

  // common titles taaki engine inhe spell na kare
  s = s
    .replace(/\bMr\b/gi, 'Mister')
    .replace(/\bMrs\b/gi, 'Missus')
    .replace(/\bMs\b/gi, 'Miss')
    .replace(/\bDr\b/gi, 'Doctor')
    .replace(/\bS\/o\b/gi, '')
    .replace(/\bW\/o\b/gi, '')
    .replace(/\bD\/o\b/gi, '')
    .replace(/\s{2,}/g, ' ')
    .trim()

  return s
}

// English number -> words (0 - 9999). Sirf tab use hota hai jab numberStyle:'words'
const ONES = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine',
  'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen',
  'eighteen', 'nineteen']
const TENS = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety']

const numToWordsEn = (n) => {
  if (!Number.isFinite(n) || n < 0) return ''
  if (n < 20) return ONES[n]
  if (n < 100) {
    const t = TENS[Math.floor(n / 10)]
    const r = n % 10
    return r ? `${t} ${ONES[r]}` : t
  }
  if (n < 1000) {
    const h = `${ONES[Math.floor(n / 100)]} hundred`
    const r = n % 100
    return r ? `${h} ${numToWordsEn(r)}` : h
  }
  const th = `${numToWordsEn(Math.floor(n / 1000))} thousand`
  const r = n % 1000
  return r ? `${th} ${numToWordsEn(r)}` : th
}

/**
 * numberStyle:
 *  'natural' (default) -> "54" jaisa hi bhejo, engine khud "fifty four" bolta hai  ✅
 *  'words'             -> "fifty four" (sirf English; agar engine numerals spell kare to)
 *  'digits'            -> "5 4" (purana behaviour, sirf bahut shor wali jagah ke liye)
 */
const formatNumber = (raw, { style = 'natural', hindi = false } = {}) => {
  const s = String(raw ?? '').trim()
  if (!s) return ''
  if (style === 'digits') return s.split('').join(' ')
  if (style === 'words' && !hindi && /^\d+$/.test(s)) return numToWordsEn(Number(s))
  return s
}

// bolne se pehle final safai: extra space/full-stop hatao taaki flow tootey na
const normalizeForSpeech = (text = '') =>
  String(text)
    .replace(/\s{2,}/g, ' ')
    .replace(/\s+([.,।])/g, '$1')
    .replace(/([.।])\1+/g, '$1')
    .trim()

/* ------------------------------------------------------------------ *
 *  HOOK
 * ------------------------------------------------------------------ */

/**
 * Patient ka naam bolne ke liye TTS hook.
 * Android WebView me speechSynthesis reliable nahi hai, isliye native plugin pehle try hota hai.
 *
 * return: { speak, primeSpeech, stop, openVoiceInstall, ready, supported }
 */
export default function useSpeak({ lang = 'hi-IN', rate = 0.9, pitch = 1, volume = 1 } = {}) {
  const [ready, setReady] = useState(false)
  const [supported, setSupported] = useState(true)
  const voiceRef = useRef(null)
  const nativeRef = useRef(null)
  const lastRef = useRef({ text: '', at: 0 })

  // ---- native (Capacitor) TTS ----
  useEffect(() => {
    let alive = true
    const isNative = !!(window.Capacitor?.isNativePlatform?.())
    if (!isNative) return
    import('@capacitor-community/text-to-speech')
      .then((m) => {
        if (!alive) return
        nativeRef.current = m.TextToSpeech
        setReady(true)
      })
      .catch(() => { /* plugin install nahi hai -> browser fallback */ })
    return () => { alive = false }
  }, [])

  // ---- browser voices (async load hoti hain) ----
  useEffect(() => {
    if (!('speechSynthesis' in window)) return
    const norm = (v) => (v.lang || '').replace('_', '-')

    const pickVoice = () => {
      const voices = window.speechSynthesis.getVoices() || []
      if (!voices.length) return
      const exact = voices.find((v) => norm(v) === lang)
      voiceRef.current =
        exact ||
        PREFERRED_LANGS.map((l) => voices.find((v) => norm(v) === l)).find(Boolean) ||
        voices.find((v) => norm(v).startsWith('en')) ||
        null
      if (!nativeRef.current) setSupported(!!exact)
      setReady(true)
    }

    pickVoice()
    window.speechSynthesis.onvoiceschanged = pickVoice
    return () => { window.speechSynthesis.onvoiceschanged = null }
  }, [lang])

  // ---- native: lang device par installed hai ya nahi ----
  useEffect(() => {
    let alive = true
    const check = async () => {
      if (!nativeRef.current) return
      try {
        const res = await nativeRef.current.isLanguageSupported({ lang })
        if (alive) setSupported(!!res?.supported)
      } catch (e) { if (alive) setSupported(true) }
    }
    check()
    return () => { alive = false }
  }, [lang, ready])

  /**
   * interrupt DEFAULT ab false hai.
   * Flush karne se pichli announcement beech-shabd me katt jati thi -> awaaz tooti-footi lagti thi.
   * Queue me lagao, ek announcement puri bolne do.
   */
  const speak = useCallback(async (text, { interrupt = false, dedupeMs = 4000 } = {}) => {
    const clean = normalizeForSpeech(text)
    if (!clean) return

    // same announcement dobara (SignalR/re-render se) -> ignore, warna line kat-kat ke bajti hai
    const now = Date.now()
    if (clean === lastRef.current.text && now - lastRef.current.at < dedupeMs) return
    lastRef.current = { text: clean, at: now }

    try {
      if (nativeRef.current) {
        await nativeRef.current.speak({
          text: clean,
          lang,
          rate,
          pitch,
          volume,
          category: 'ambient',
          queueStrategy: interrupt ? QUEUE_FLUSH : QUEUE_ADD,
        })
        return
      }
      if (!('speechSynthesis' in window)) return
      if (interrupt) window.speechSynthesis.cancel()
      const u = new SpeechSynthesisUtterance(clean)
      if (voiceRef.current) u.voice = voiceRef.current
      u.lang = voiceRef.current?.lang || lang
      u.rate = rate
      u.pitch = pitch
      u.volume = volume
      window.speechSynthesis.speak(u)
    } catch (e) {
      console.warn('tts error', e)
    }
  }, [lang, rate, pitch, volume])

  // pehle tap par silent utterance -> browser TTS unlock ho jata hai (native me zaroorat nahi)
  const primeSpeech = useCallback(() => {
    if (nativeRef.current) return
    if (!('speechSynthesis' in window)) return
    try {
      const u = new SpeechSynthesisUtterance(' ')
      u.volume = 0
      window.speechSynthesis.speak(u)
    } catch (e) { /* ignore */ }
  }, [])

  const stop = useCallback(() => {
    lastRef.current = { text: '', at: 0 }
    try { window.speechSynthesis?.cancel() } catch (e) { /* ignore */ }
    try { nativeRef.current?.stop() } catch (e) { /* ignore */ }
  }, [])

  // device par voice data missing ho to Android ka install screen khol dein (sirf native)
  const openVoiceInstall = useCallback(async () => {
    try { await nativeRef.current?.openInstall() } catch (e) { /* ignore */ }
  }, [])

  useEffect(() => () => { stop() }, [stop])

  return { speak, primeSpeech, stop, openVoiceInstall, ready, supported }
}

/**
 * Announcement text banata hai.
 *
 * numberStyle: 'natural' (default) | 'words' | 'digits'
 * repeatNumber: number do baar bolo — digits me todne se behtar clarity, aur shabd pura rehta hai
 *
 * Full stop ki jagah comma: full stop par engine lamba pause deta hai,
 * isliye line "chopped" sunai deti thi.
 */
export const buildAnnouncement = (data, kind = 'cabin', {
  hindi = true,
  numberStyle = 'natural',
  repeatNumber = true,
} = {}) => {
  if (!data) return ''
  const name = cleanName(data.patientName)
  const num = formatNumber(data.confirmedAllotedNumber, { style: numberStyle, hindi })

  if (hindi) {
    const head = num
      ? (repeatNumber ? `नंबर ${num}, नंबर ${num},` : `नंबर ${num},`)
      : ''
    return kind === 'cabin'
      ? normalizeForSpeech(`${head} ${name}, कृपया डॉक्टर के केबिन में आइए।`)
      : normalizeForSpeech(`${head} ${name}, कृपया तैयार रहें, आपका नंबर अगला है।`)
  }

  const head = num
    ? (repeatNumber ? `Number ${num}, number ${num},` : `Number ${num},`)
    : ''
  return kind === 'cabin'
    ? normalizeForSpeech(`${head} ${name}, please come to the doctor's cabin.`)
    : normalizeForSpeech(`${head} ${name}, please get ready, you are next.`)
}